import { useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw, Loader2, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { useJob, isRunning } from "../../lib/useJob";
import { Btn, Card } from "../kit/Kit";
import { AIProgress } from "../kit/AIProgress";

export const SECCIONES = [
  ["resumen_ejecutivo", "Resumen ejecutivo", "Primer párrafo: recomendación. Después viñetas «- » de por qué sí."],
  ["en_una_mirada", "En una mirada", "Párrafos cortos separados por una línea en blanco."],
  ["reciprocidad", "Reciprocidad", "Cómo funciona y qué vale para Trafalgar."],
  ["riesgos_mitigantes", "Riesgos y mitigantes", "Una línea por riesgo: «- Riesgo → Mitigante»."],
  ["condiciones_precedentes", "Condiciones precedentes", "Una línea por condición: «- condición». Incluye las de alertas."],
  ["opinion_comercial", "Opinión comercial", "Opinión del analista para el Comité."],
];

export const dictamenCompleto = (d) => !!d && SECCIONES.every(([k]) => (d.secciones?.[k] || "").trim());

const AutoText = ({ value, onChange, disabled, testid }) => {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + 2}px`; } }, [value]);
  return <textarea ref={ref} data-testid={testid} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
    className="min-h-[120px] w-full resize-none rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] px-4 py-3 text-sm leading-relaxed outline-none transition-colors focus:border-[#E8643C] focus:bg-white disabled:opacity-70" />;
};

export const StepDictamen = ({ id, exp, setExp, onDictamen, ro }) => {
  const [jid, setJid] = useState(exp.dictamen_job_id);
  const [target, setTarget] = useState(null);
  const job = useJob(id, jid, async (j) => {
    if (j.status === "error") { toast.error(`La IA no pudo redactar: ${j.message}`); return; }
    const r = await http.get(`/expedientes/${id}`);
    setExp((e) => ({ ...e, dictamen: r.data.dictamen, dictamen_job_id: r.data.dictamen_job_id }));
    toast.success("Dictamen actualizado");
  });
  const running = isRunning(job);
  const d = exp.dictamen;

  const gen = async (sec) => {
    const editadas = d?.editadas || [];
    if (sec && editadas.includes(sec) && !window.confirm("Esta sección tiene ediciones manuales. ¿Sobrescribirla con un texto nuevo de la IA?")) return;
    if (!sec && editadas.length && !window.confirm("Hay secciones editadas manualmente. ¿Regenerar todo el dictamen y sobrescribirlas?")) return;
    try {
      const r = await http.post(`/expedientes/${id}/dictamen`, { seccion: sec || null });
      setTarget(sec || "all");
      setJid(r.data.job_id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const Progress = () => running && (
    <div className="mt-4" data-testid="dictamen-progress"><AIProgress job={job} title="Dictamen con IA" compact testid="dictamen-ai-progress" /></div>
  );

  if (!d) {
    return (
      <Card title="Dictamen del analista" subtitle="Claude Sonnet 5.5 redacta con los datos del expediente, el resultado del motor y las alertas de inconsistencia." testid="step-dictamen">
        <div className="rounded-2xl border border-dashed border-[#E2DDD5] p-10 text-center">
          <Sparkles className="mx-auto text-[#E8643C]" />
          <p className="mx-auto mt-3 max-w-md text-sm text-[#5C5850]">Seis secciones editables: resumen ejecutivo, en una mirada, reciprocidad, riesgos y mitigantes, condiciones precedentes y opinión comercial. Lo que edites es lo que va al PDF.</p>
          <Btn kind="accent" className="mt-6" disabled={ro || running} onClick={() => gen(null)} data-testid="generar-dictamen-button">
            {running ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generar dictamen con IA
          </Btn>
          <Progress />
        </div>
      </Card>
    );
  }
  return (
    <>
      <Card title="Dictamen del analista" testid="step-dictamen"
        subtitle={`Generado ${new Date(d.generado_at).toLocaleString("es-MX")} · ${d.editadas.length} secciones editadas a mano. El texto editado es el que va al PDF.`}
        right={<Btn kind="secondary" disabled={ro || running} onClick={() => gen(null)} data-testid="regenerar-dictamen-button"><RefreshCw size={15} /> Regenerar todo</Btn>}>
        <Progress />
      </Card>
      {SECCIONES.map(([k, label, help]) => {
        const busy = running && (target === k || target === "all");
        return (
          <Card key={k} testid={`dictamen-seccion-${k}`} title={label} subtitle={help}
            right={
              <div className="flex items-center gap-3">
                {d.editadas.includes(k) && <span className="flex items-center gap-1 text-xs font-semibold text-amber-700" data-testid={`dictamen-editada-${k}`}><PencilLine size={13} /> Editado</span>}
                <Btn kind="ghost" className="px-3 py-2" disabled={ro || running} onClick={() => gen(k)} data-testid={`regenerar-seccion-${k}`}>
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Regenerar
                </Btn>
              </div>}>
            <AutoText testid={`dictamen-text-${k}`} value={d.secciones[k] || ""} disabled={ro || running} onChange={(v) => onDictamen(k, v)} />
          </Card>
        );
      })}
    </>
  );
};
